import { cn } from "@/lib/utils";
import { useMemo } from "react";
import type { Components } from "streamdown";
import { Streamdown } from "streamdown";
import type { ChatMessage } from "../hooks/chat-message";
import { CitationPill } from "./citation-pill";

interface MessageBubbleProps {
  message: ChatMessage;
  isActive: boolean;
  onCitePillClick: (citationNumber: number) => void;
  onActivate: () => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isActive,
  onCitePillClick,
  onActivate,
}) => {
  const isUser = message.role === "user";
  const hasCitations = !isUser && !!message.citations?.length;
  const isWaitingFirst = !isUser && message.isStreaming && !message.content;

  const handleCiteClick = (citationNumber: number) => {
    onActivate();
    onCitePillClick(citationNumber);
  };

  const citationIdMap = message.citationIdMap;

  // cite is an allowed custom tag via allowedTags; cast needed because
  // Streamdown types <cite> as an intrinsic HTML element without the custom `n` attr.
  const streamdownComponents = useMemo(() => {
    const Cite: React.FC<{ n?: string }> = ({ n: nAttr }) => {
      const num = Number(nAttr);
      if (!Number.isFinite(num)) return null;
      const domId = citationIdMap?.[num];
      return (
        <CitationPill
          n={num}
          missing={!domId}
          onClick={domId ? () => handleCiteClick(num) : undefined}
        />
      );
    };
    Cite.displayName = "Cite";
    return { cite: Cite } as unknown as Components;
  }, [citationIdMap]);

  return (
    <div className={cn("flex mb-4", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "rounded-[14px] px-3.5 py-[11px] max-w-[78%] body-2-normal-regular leading-relaxed break-words",
          isUser
            ? "bg-neutral-900 text-white whitespace-pre-wrap"
            : "bg-background-neutral-neutral text-label-normal cs-rag-bubble",
          hasCitations && "cursor-pointer",
          hasCitations && isActive && "ring-1 ring-blue-600/36",
        )}
        onClick={hasCitations ? onActivate : undefined}
      >
        {isWaitingFirst ? (
          <span className="text-label-assistive">답변 생성 중…</span>
        ) : isUser ? (
          message.content
        ) : (
          <>
            <Streamdown
              mode={message.isStreaming ? "streaming" : "static"}
              animated={
                message.isStreaming
                  ? { animation: "fadeIn", duration: 120, sep: "word" }
                  : false
              }
              isAnimating={message.isStreaming}
              caret={message.isStreaming ? "block" : undefined}
              allowedTags={{ cite: ["n"] }}
              components={streamdownComponents}
            >
              {message.content}
            </Streamdown>
            {message.streamError && (
              <p className="mt-2 caption-2-regular text-red-500">
                오류: {message.streamError}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
